"use client";

import {
  adminClient,
  organizationClient,
  lastLoginMethodClient,
} from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

import { DEBUG_AUTH } from "@/lib/environments";

/**
 * Better Auth React Client
 *
 * Client-side authentication hooks and helpers for React components.
 * Use these hooks in client components for type-safe session access.
 *
 * Key hooks:
 * - useSession() - Get current session with loading/error states
 * - signIn() - Sign in with OAuth provider
 * - signOut() - Sign out current user
 * - admin.* - Admin plugin methods (impersonate, etc.)
 *
 * @see ADR-003: Auth Guard Component Pattern
 * @see ADR-004: Simplified Session Data Model
 */
if (DEBUG_AUTH) {
  console.debug({
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_TARGET_ENV: process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV,
    PORT: process.env.PORT,
    VERCEL_URL: process.env.VERCEL_URL,
    NEXT_PUBLIC_VERCEL_BRANCH_URL: process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL,
    NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
    NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL:
      process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL,
  });
}
export const authClient = createAuthClient({
  baseURL: process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL // so preview and production deployments
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}`
    : `http://localhost:${process.env.PORT || 3000}`,
  plugins: [
    adminClient(),
    organizationClient(),
    lastLoginMethodClient({
      cookieName: "auth.last_used_login_method",
    }),
  ],
  fetchOptions: {
    credentials: "include",
  },
});

// Export hooks for client components
export const {
  useSession,
  signIn,
  signOut,
  admin,
  organization,
  getLastUsedLoginMethod,
  clearLastUsedLoginMethod,
  isLastUsedLoginMethod,
} = authClient;

// Export admin methods for convenience
export const {
  impersonateUser,
  stopImpersonating,
  banUser,
  unbanUser,
  removeUser,
  listUserSessions,
  revokeUserSession,
  revokeUserSessions,
} = admin;

// Export organization methods for convenience
export const {
  inviteMember,
  acceptInvitation,
  rejectInvitation,
  cancelInvitation,
  // Note: These methods exist on organization object but TypeScript doesn't recognize them
  // Use organization.addMember(), organization.removeMember(), etc. directly instead
  // addMember,
  // removeMember,
  // updateMemberRole,
  // setActive,
  // useActiveOrganization,
} = organization;

// Re-export types from server config and extensions
export type { Session, User } from "./better-auth";
export type { SessionUser, UserLike } from "./types";

// Re-export permission hooks
export {
  usePlatformPermissions,
  useBusinessPermissions,
  // usePermissions,
  type PlatformPermission,
  type BusinessPermission,
} from "./permissions";
